import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

// ─── Constants ──────────────────────────────────────────────────────────────
const FREE_LIMIT       = 5;
const SESSION_KEY      = 'ca_tests_used';   // sessionStorage — resets on tab close
const REDIRECT_KEY     = 'redirectAfterLogin';

// ─── Helpers ─────────────────────────────────────────────────────────────────
const getUsedCount  = () => parseInt(sessionStorage.getItem(SESSION_KEY) || '0', 10);
const incUsedCount  = () => sessionStorage.setItem(SESSION_KEY, getUsedCount() + 1);
const formatTime    = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

// ─── Sub-components ──────────────────────────────────────────────────────────

// Palette button for question navigation
const PaletteBtn = ({ idx, status, isCurrent, onClick }) => {
  const cls = {
    answered:    'bg-green-500 text-white',
    skipped:     'bg-red-500 text-white',
    current:     'bg-blue-600 text-white ring-2 ring-white ring-offset-1 ring-offset-[#1a0b2e]',
    unvisited:   'bg-white/10 text-text-muted',
  }[isCurrent ? 'current' : status] || 'bg-white/10 text-text-muted';

  return (
    <button
      onClick={onClick}
      className={`w-9 h-9 rounded-lg font-bold text-sm transition-all ${cls}`}
    >
      {idx + 1}
    </button>
  );
};

// Review card after submission
const ReviewCard = ({ q, idx, userOptId }) => {
  const isSkipped = userOptId === undefined;
  const userOpt   = q.options.find(o => o.OptionId === userOptId);
  const isCorrect = !!userOpt?.IsCorrect;

  return (
    <div className={`glass-card mb-4 border-l-4 ${isSkipped ? 'border-gray-500' : isCorrect ? 'border-green-500' : 'border-red-500'}`}>
      <div className="flex items-center gap-2 mb-3">
        <span className={`text-xs font-bold px-2 py-0.5 rounded ${isSkipped ? 'bg-gray-500/20 text-gray-400' : isCorrect ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
          Q{idx + 1} — {isSkipped ? 'Skipped' : isCorrect ? 'Correct' : 'Wrong'}
        </span>
      </div>
      <div className="text-text-light text-sm mb-3 leading-relaxed" dangerouslySetInnerHTML={{ __html: q.Question }} />
      <div className="space-y-2">
        {q.options.map(opt => {
          const isUserChoice   = opt.OptionId === userOptId;
          const isCorrectOpt   = opt.IsCorrect == 1;
          let cls = 'border-glass-border bg-white/5 text-text-muted';
          if (isCorrectOpt && isUserChoice)  cls = 'border-green-500 bg-green-500/10 text-green-300';
          else if (isCorrectOpt)             cls = 'border-green-400 bg-green-500/5 text-green-400';
          else if (isUserChoice)             cls = 'border-red-400 bg-red-500/10 text-red-300';
          return (
            <div key={opt.OptionId} className={`flex items-center gap-3 px-3 py-2 rounded-xl border text-sm ${cls}`}>
              <div className={`w-4 h-4 rounded-full border flex-shrink-0 ${isCorrectOpt ? 'border-green-500 bg-green-500' : isUserChoice ? 'border-red-400 bg-red-400' : 'border-current'}`} />
              <span dangerouslySetInnerHTML={{ __html: opt.OptionText }} />
              <div className="ml-auto flex gap-1 flex-shrink-0">
                {isUserChoice  && <span className="text-xs bg-blue-500/20 text-blue-300 px-1.5 rounded">Your answer</span>}
                {isCorrectOpt && !isUserChoice && <span className="text-xs bg-green-500/20 text-green-400 px-1.5 rounded">Correct</span>}
              </div>
            </div>
          );
        })}
      </div>
      {q.explanation && (
        <div className="mt-3 p-3 bg-blue-500/10 border-l-4 border-blue-500 rounded-r-xl">
          <p className="text-xs font-bold text-blue-400 mb-1">💡 Explanation</p>
          <div className="text-xs text-text-muted" dangerouslySetInnerHTML={{ __html: q.explanation }} />
        </div>
      )}
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────
const CurrentAffairsTestEngine = () => {
  const { user } = useAuth();
  const navigate  = useNavigate();

  // ── Phase management ──
  // 'lobby' → 'testing' → 'result' → (gate if limit hit)
  const [phase, setPhase]             = useState('lobby');

  // ── Available tests ──
  const [availableTests, setAvailableTests] = useState([]);
  const [usedIds, setUsedIds]               = useState([]); // IDs already taken this session
  const [testsUsed, setTestsUsed]           = useState(getUsedCount);

  // ── Test state ──
  const [quiz, setQuiz]               = useState(null);
  const [questions, setQuestions]     = useState([]);
  const [currentIdx, setCurrentIdx]   = useState(0);
  const [answers, setAnswers]         = useState({});
  const [visited, setVisited]         = useState(new Set([0]));
  const [timeLeft, setTimeLeft]       = useState(0);
  const [loadingTest, setLoadingTest] = useState(false);
  const [submitting, setSubmitting]   = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  // ── Result state ──
  const [result, setResult]   = useState(null);
  const [review, setReview]   = useState([]);
  const [showReview, setShowReview] = useState(false);

  // ── Load available tests ──
  useEffect(() => {
    api.get('/current-affairs/tests')
      .then(res => { if (res.data.success) setAvailableTests(res.data.data); })
      .catch(() => {});
  }, []);

  // ── Timer ──
  useEffect(() => {
    if (phase !== 'testing' || timeLeft <= 0) return;
    const id = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) { clearInterval(id); handleSubmit(true); return 0; }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [phase, timeLeft]);

  // ── Track visited questions ──
  useEffect(() => {
    if (phase === 'testing') setVisited(prev => new Set(prev).add(currentIdx));
  }, [currentIdx, phase]);

  // ── Pick next unused quiz ──
  const getNextQuiz = useCallback(() => {
    return availableTests.find(t => !usedIds.includes(t.QuizId)) || availableTests[0] || null;
  }, [availableTests, usedIds]);

  // ── Start a test ──
  const startTest = async (quizId) => {
    setLoadingTest(true);
    try {
      const res = await api.get(`/current-affairs/test/${quizId}/start`);
      if (!res.data.success) throw new Error();
      setQuiz(res.data.quiz);
      setQuestions(res.data.questions);
      setTimeLeft((res.data.quiz.maxTime || res.data.quiz.MaxTime || 30) * 60);
      setCurrentIdx(0);
      setAnswers({});
      setVisited(new Set([0]));
      setPhase('testing');
    } catch {
      alert('Failed to load test. Please try again.');
    } finally {
      setLoadingTest(false);
    }
  };

  // ── Submit ──
  const handleSubmit = async (auto = false) => {
    if (!auto) {
      // eslint-disable-next-line no-restricted-globals
      if (!confirm('Are you sure you want to finish the test?')) return;
    }
    setSubmitting(true);
    try {
      const res = await api.post(`/current-affairs/test/${quiz.QuizId}/submit`, { userAnswers: answers });
      if (res.data.success) {
        const newCount = testsUsed + 1;
        incUsedCount();
        setTestsUsed(newCount);
        setUsedIds(prev => [...prev, quiz.QuizId]);
        setResult(res.data.data);
        setReview(res.data.review);
        setShowReview(false);
        setPhase('result');
      }
    } catch {
      alert('Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOptionSelect = (qId, optId) => setAnswers(prev => ({ ...prev, [qId]: optId }));

  const handleNext = () => {
    if (currentIdx < questions.length - 1) setCurrentIdx(i => i + 1);
    else handleSubmit();
  };

  const handleTakeAnother = () => {
    const next = getNextQuiz();
    if (next) { setShowReview(false); startTest(next.QuizId); }
  };

  const handleLoginRedirect = () => {
    sessionStorage.setItem(REDIRECT_KEY, '/current-affairs');
    navigate('/login');
  };

  // ─── PHASE: LOBBY ────────────────────────────────────────────────────────
  if (phase === 'lobby') {
    const atLimit = testsUsed >= FREE_LIMIT && !user;
    const nextTest = getNextQuiz();

    return (
      <div className="min-h-screen pt-32 pb-20">
        <div className="max-w-2xl mx-auto px-4 sm:px-8 text-center">
          <div className="hero-badge mx-auto mb-6">🌍 Current Affairs Quiz</div>
          <h1 className="text-4xl font-extrabold gradient-text mb-4">Free Current Affairs Test</h1>
          <p className="text-text-muted mb-2">
            Test your awareness with MCQ questions on current events. Instant results and answer review included.
          </p>

          {/* Progress bar for free tests */}
          {!user && (
            <div className="glass-card max-w-sm mx-auto mt-6 mb-6 text-left">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-text-muted">Free Tests Used</span>
                <span className={testsUsed >= FREE_LIMIT ? 'text-red-400 font-bold' : 'text-accent-gold font-bold'}>
                  {testsUsed} / {FREE_LIMIT}
                </span>
              </div>
              <div className="w-full bg-white/10 rounded-full h-2 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${testsUsed >= FREE_LIMIT ? 'bg-red-500' : 'bg-accent-gold'}`}
                  style={{ width: `${Math.min(100, (testsUsed / FREE_LIMIT) * 100)}%` }}
                />
              </div>
              {!atLimit && (
                <p className="text-xs text-text-muted mt-2">
                  {FREE_LIMIT - testsUsed} free test{FREE_LIMIT - testsUsed !== 1 ? 's' : ''} remaining. Login for unlimited access.
                </p>
              )}
            </div>
          )}

          {atLimit ? (
            // ── Free limit reached ──
            <div className="glass-card mt-6">
              <div className="text-5xl mb-4">🔒</div>
              <h2 className="text-2xl font-bold text-text-light mb-2">You've used all {FREE_LIMIT} free tests!</h2>
              <p className="text-text-muted mb-6">
                Login or register to unlock unlimited current affairs tests and track your performance over time.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button onClick={handleLoginRedirect} className="btn-primary px-8 py-3 font-bold rounded-xl">
                  🔑 Login to Continue
                </button>
                <Link
                  to="/register"
                  onClick={() => sessionStorage.setItem(REDIRECT_KEY, '/current-affairs')}
                  className="px-8 py-3 font-bold rounded-xl border border-glass-border text-text-light hover:bg-white/5 transition-colors"
                >
                  📝 Register Free
                </Link>
              </div>
            </div>
          ) : availableTests.length === 0 ? (
            <div className="glass-card mt-6">
              <p className="text-text-muted">No tests available at the moment. Check back soon!</p>
            </div>
          ) : (
            <div className="glass-card mt-6">
              {nextTest ? (
                <>
                  <div className="grid grid-cols-3 gap-4 mb-6 text-center">
                    <div className="bg-bg-secondary rounded-xl p-3">
                      <div className="text-xl font-bold text-accent-gold">{nextTest.MaxNOQ || '?'}</div>
                      <div className="text-xs text-text-muted">Questions</div>
                    </div>
                    <div className="bg-bg-secondary rounded-xl p-3">
                      <div className="text-xl font-bold text-accent-gold">{nextTest.MaxTime || 30} min</div>
                      <div className="text-xs text-text-muted">Time Limit</div>
                    </div>
                    <div className="bg-bg-secondary rounded-xl p-3">
                      <div className="text-xl font-bold text-accent-gold">{nextTest.TotalMarks || '?'}</div>
                      <div className="text-xs text-text-muted">Total Marks</div>
                    </div>
                  </div>
                  <button
                    onClick={() => startTest(nextTest.QuizId)}
                    disabled={loadingTest}
                    className="btn-primary w-full py-4 text-lg font-bold rounded-xl disabled:opacity-50"
                  >
                    {loadingTest ? '⏳ Loading...' : '🚀 Start Current Affairs Test'}
                  </button>
                  <p className="text-xs text-text-muted mt-3">No login required · Instant results & review</p>
                </>
              ) : (
                <p className="text-text-muted">You've taken all available tests! Check back for new ones.</p>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ─── PHASE: TESTING ──────────────────────────────────────────────────────
  if (phase === 'testing') {
    const currentQ  = questions[currentIdx];
    if (!currentQ)  return <div className="min-h-screen flex items-center justify-center text-text-muted">Loading...</div>;

    const getStatus = (idx, qId) => {
      if (answers[qId] !== undefined) return 'answered';
      if (visited.has(idx)) return 'skipped';
      return 'unvisited';
    };

    const answeredCount  = Object.keys(answers).length;
    const remainingCount = questions.length - answeredCount;

    return (
      <div className="h-screen mt-20 bg-[#1a0b2e] flex flex-col overflow-hidden text-white font-sans">

        {/* Top bar */}
        <div className="bg-[#0f0720]/90 border-b border-white/10 px-3 py-3 flex justify-between items-center gap-3 z-20">
          <div className="min-w-0 flex-1">
            <p className="font-bold text-sm truncate">{quiz?.title || quiz?.Title || 'Current Affairs Test'}</p>
            <p className="text-xs text-text-muted">Question {currentIdx + 1} of {questions.length}</p>
          </div>
          <div className={`font-mono font-bold text-lg px-3 py-1.5 rounded-lg border ${timeLeft < 120 ? 'bg-red-500/20 border-red-500 text-red-300' : 'bg-white/10 border-white/20 text-white'}`}>
            ⏱ {formatTime(timeLeft)}
          </div>
          <button
            onClick={() => handleSubmit(false)}
            disabled={submitting}
            className="bg-red-600 hover:bg-red-700 px-3 py-2 rounded-lg text-white font-bold text-sm flex-shrink-0 disabled:opacity-50"
          >
            {submitting ? '...' : 'Submit'}
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 flex overflow-hidden">

          {/* Question area */}
          <div className="flex-1 overflow-y-auto bg-white">
            <div className="max-w-3xl mx-auto px-4 sm:px-8 py-6">

              {/* Q header */}
              <div className="flex justify-between items-center mb-5 border-b border-gray-200 pb-4">
                <span className="text-lg font-bold text-gray-800">Q {currentIdx + 1}</span>
                <div className="flex gap-2">
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded font-semibold">+{currentQ.Marks || 1}</span>
                </div>
              </div>

              {/* Question text */}
              <div className="text-base sm:text-lg mb-6 text-gray-900 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: currentQ.Question }} />

              {/* Options */}
              <div className="space-y-3 mb-10">
                {currentQ.options.map(opt => (
                  <label key={opt.OptionId}
                    className={`flex items-center gap-3 p-3 sm:p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      answers[currentQ.QuesId] === opt.OptionId
                        ? 'border-purple-600 bg-purple-50'
                        : 'border-gray-200 hover:border-gray-300 bg-gray-50'
                    }`}>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                      answers[currentQ.QuesId] === opt.OptionId ? 'border-purple-600 bg-purple-600' : 'border-gray-400 bg-white'
                    }`}>
                      {answers[currentQ.QuesId] === opt.OptionId && <div className="w-2 h-2 bg-white rounded-full" />}
                    </div>
                    <div className="text-gray-900 font-medium text-sm sm:text-base"
                      dangerouslySetInnerHTML={{ __html: opt.OptionText }} />
                    <input type="radio" name={`q-${currentQ.QuesId}`} className="hidden"
                      onChange={() => handleOptionSelect(currentQ.QuesId, opt.OptionId)}
                      checked={answers[currentQ.QuesId] === opt.OptionId} />
                  </label>
                ))}
              </div>

              {/* Navigation */}
              <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                <button onClick={() => setCurrentIdx(i => Math.max(0, i - 1))} disabled={currentIdx === 0}
                  className="px-4 py-2 rounded-lg bg-gray-200 text-gray-700 font-bold text-sm hover:bg-gray-300 disabled:opacity-50">
                  ← Prev
                </button>
                <button onClick={handleNext} disabled={submitting}
                  className={`px-6 py-2 rounded-lg font-bold text-white text-sm ${
                    currentIdx === questions.length - 1 ? 'bg-green-600 hover:bg-green-700' : 'bg-purple-700 hover:bg-purple-800'
                  }`}>
                  {currentIdx === questions.length - 1 ? '✓ Finish' : 'Save & Next →'}
                </button>
              </div>

              <div className="h-8 lg:hidden" />
            </div>
          </div>

          {/* Desktop palette */}
          <div className="hidden lg:flex w-64 bg-gray-50 border-l border-gray-200 flex-col">
            <div className="p-4 border-b border-gray-200 bg-white">
              <h3 className="font-bold text-gray-800 mb-2 text-sm">Question Palette</h3>
              <div className="flex gap-3 text-xs flex-wrap">
                <span className="flex items-center gap-1"><span className="w-3 h-3 bg-green-500 rounded inline-block" /> Answered</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-500 rounded inline-block" /> Skipped</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 bg-white border border-gray-400 rounded inline-block" /> Not visited</span>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-3">
              <div className="grid grid-cols-5 gap-1.5">
                {questions.map((q, i) => (
                  <PaletteBtn key={q.QuesId} idx={i}
                    status={getStatus(i, q.QuesId)}
                    isCurrent={currentIdx === i}
                    onClick={() => setCurrentIdx(i)} />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile palette */}
        <div className="lg:hidden">
          {paletteOpen && <div className="fixed inset-0 bg-black/40 z-30" onClick={() => setPaletteOpen(false)} />}
          <div className={`fixed bottom-0 left-0 right-0 z-40 bg-white rounded-t-2xl shadow-2xl transition-transform duration-300 ${paletteOpen ? 'translate-y-0' : 'translate-y-full'}`}
            style={{ maxHeight: '65vh' }}>
            <div className="flex justify-center pt-2 pb-1"><div className="w-10 h-1 bg-gray-300 rounded-full" /></div>
            <div className="flex justify-between items-center px-4 pb-3 border-b border-gray-100">
              <h3 className="font-bold text-gray-800 text-sm">Question Palette</h3>
              <button onClick={() => setPaletteOpen(false)} className="text-gray-400 text-xl">×</button>
            </div>
            <div className="overflow-y-auto p-4" style={{ maxHeight: 'calc(65vh - 90px)' }}>
              <div className="grid grid-cols-6 gap-2">
                {questions.map((q, i) => (
                  <PaletteBtn key={q.QuesId} idx={i}
                    status={getStatus(i, q.QuesId)}
                    isCurrent={currentIdx === i}
                    onClick={() => { setCurrentIdx(i); setPaletteOpen(false); }} />
                ))}
              </div>
            </div>
          </div>
          {/* Mobile bottom bar */}
          <div className="fixed bottom-0 left-0 right-0 z-20 bg-white border-t border-gray-200 px-4 py-2 flex items-center gap-3 shadow-lg">
            <div className="flex gap-3 text-xs font-semibold">
              <span className="flex items-center gap-1 text-green-700"><span className="w-2.5 h-2.5 rounded-sm bg-green-500 inline-block" />{answeredCount}</span>
              <span className="flex items-center gap-1 text-gray-500"><span className="w-2.5 h-2.5 rounded-sm bg-gray-300 border border-gray-400 inline-block" />{remainingCount}</span>
            </div>
            <button onClick={() => setPaletteOpen(true)}
              className="flex-1 py-2 bg-[#1a0b2e] text-white rounded-lg font-semibold text-sm flex items-center justify-center gap-2">
              ☰ Q Palette ({currentIdx + 1}/{questions.length})
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── PHASE: RESULT ────────────────────────────────────────────────────────
  if (phase === 'result' && result) {
    const atLimit     = testsUsed >= FREE_LIMIT && !user;
    const hasMore     = availableTests.some(t => !usedIds.includes(t.QuizId));
    const pct         = quiz ? Math.round((result.totalMarks / ((quiz.maxNOQ || quiz.MaxNOQ || 1) * 1)) * 100) : 0;

    return (
      <div className="min-h-screen pt-32 pb-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">

          {/* Score card */}
          <div className="glass-card text-center mb-6">
            <div className="text-5xl mb-3">{pct >= 70 ? '🏆' : pct >= 40 ? '📊' : '📝'}</div>
            <h2 className="text-3xl font-extrabold gradient-text mb-1">Test Complete!</h2>
            <p className="text-text-muted mb-6">{quiz?.title || quiz?.Title || 'Current Affairs Test'}</p>

            <div className="text-5xl font-extrabold text-accent-gold mb-1">{result.totalMarks}</div>
            <p className="text-text-muted text-sm mb-6">out of {quiz?.maxNOQ || quiz?.MaxNOQ} marks</p>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              {[
                { v: result.correctCount,   l: '✓ Correct',  c: 'text-green-400' },
                { v: result.wrongCount,     l: '✗ Wrong',    c: 'text-red-400'   },
                { v: result.attemptedCount, l: 'Attempted',  c: 'text-blue-400'  },
                { v: result.skippedCount,   l: 'Skipped',    c: 'text-gray-400'  },
              ].map(s => (
                <div key={s.l} className="bg-bg-secondary rounded-xl p-3">
                  <div className={`text-2xl font-bold ${s.c}`}>{s.v}</div>
                  <div className="text-xs text-text-muted">{s.l}</div>
                </div>
              ))}
            </div>

            {/* Free tests counter */}
            {!user && (
              <div className="mb-6">
                <div className="flex justify-between text-sm mb-1.5">
                  <span className="text-text-muted">Free Tests Used</span>
                  <span className={testsUsed >= FREE_LIMIT ? 'text-red-400 font-bold' : 'text-accent-gold font-bold'}>
                    {testsUsed} / {FREE_LIMIT}
                  </span>
                </div>
                <div className="w-full bg-white/10 rounded-full h-2 overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${testsUsed >= FREE_LIMIT ? 'bg-red-500' : 'bg-accent-gold'}`}
                    style={{ width: `${Math.min(100, (testsUsed / FREE_LIMIT) * 100)}%` }} />
                </div>
              </div>
            )}

            {/* CTA buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => setShowReview(v => !v)}
                className="px-6 py-3 rounded-xl border border-glass-border text-text-light hover:bg-white/5 transition-colors font-semibold"
              >
                {showReview ? '▲ Hide Review' : '📋 Review Answers'}
              </button>

              {atLimit ? (
                <>
                  <button onClick={handleLoginRedirect} className="btn-primary px-6 py-3 font-bold rounded-xl">
                    🔑 Login for More Tests
                  </button>
                  <Link to="/register"
                    onClick={() => sessionStorage.setItem(REDIRECT_KEY, '/current-affairs')}
                    className="px-6 py-3 font-bold rounded-xl border border-glass-border text-text-light hover:bg-white/5 transition-colors">
                    📝 Register Free
                  </Link>
                </>
              ) : hasMore ? (
                <button onClick={handleTakeAnother} disabled={loadingTest}
                  className="btn-primary px-6 py-3 font-bold rounded-xl disabled:opacity-50">
                  {loadingTest ? '⏳ Loading...' : '🔄 Take Another Test?'}
                </button>
              ) : (
                <button onClick={() => setPhase('lobby')} className="btn-primary px-6 py-3 font-bold rounded-xl">
                  ↩ Back to Tests
                </button>
              )}
            </div>

            {/* Login nudge when close to limit */}
            {!user && testsUsed >= FREE_LIMIT - 1 && testsUsed < FREE_LIMIT && (
              <p className="text-xs text-accent-gold mt-4">
                ⚡ 1 free test remaining — <button onClick={handleLoginRedirect} className="underline hover:no-underline">login now</button> for unlimited access
              </p>
            )}
          </div>

          {/* Answer review */}
          {showReview && (
            <div>
              <h3 className="text-xl font-bold text-text-light mb-4 flex items-center gap-2">
                <span className="w-1 h-6 bg-accent-gold rounded-full inline-block" />
                Answer Review
              </h3>
              {review.map((q, i) => (
                <ReviewCard key={q.QuesId} q={q} idx={i} userOptId={answers[q.QuesId]} />
              ))}
            </div>
          )}

          <div className="text-center mt-8">
            <Link to="/current-affairs" className="text-text-muted hover:text-text-light text-sm transition-colors">
              ← Back to Current Affairs
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default CurrentAffairsTestEngine;